import type { ReactNode } from 'react';
import * as RDialog from '@radix-ui/react-dialog';
import { Button } from './Button';
import './Dialog.css';

export interface DialogProps {
  open: boolean;
  /** Called when the user dismisses (ESC / overlay / close button). */
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Block dismissal while a mutation is in flight. */
  busy?: boolean;
  size?: 'default' | 'wide';
}

// Radix-powered modal — same prop surface as the legacy <Modal> so call sites
// swap 1:1, but with real focus-trap, scroll-lock, ESC and aria-modal handling.
// Reuses the existing .modal-header/.modal-body/.modal-footer/.modal-close
// layout classes; only the overlay/panel positioning is its own (Radix renders
// the panel as a portal sibling, not a child of the overlay).
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  busy = false,
  size = 'default',
}: DialogProps): JSX.Element {
  return (
    <RDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <RDialog.Portal>
        <RDialog.Overlay className="dsr-dialog-overlay" />
        <RDialog.Content
          className={['dsr-dialog-panel', size === 'wide' ? 'is-wide' : ''].filter(Boolean).join(' ')}
          onInteractOutside={(e) => {
            if (busy) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (busy) e.preventDefault();
          }}
        >
          <header className="modal-header">
            <RDialog.Title asChild>
              <h2>{title}</h2>
            </RDialog.Title>
            <RDialog.Close asChild>
              <button type="button" className="modal-close" disabled={busy} aria-label="Close">
                ×
              </button>
            </RDialog.Close>
          </header>
          <div className="modal-body">{children}</div>
          {footer && <footer className="modal-footer">{footer}</footer>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Render the confirm action as destructive. */
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// The "are you sure?" gate before a destructive/irreversible action.
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={busy}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    >
      <p className="confirm-message">{message}</p>
    </Dialog>
  );
}
